
import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import {
  LoadState,
  LOG_MODULES,
  MODULE_IDS,
  MODULE_NAMES,
} from "../../../../../../constants/enums";
import { StandadCard } from "../../../../../../components/Cards";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  SelectChangeEvent,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  useDispatchWrapper,
  usePoliciesByCustomer,
} from "../../../../../../hooks";
import { useFormik } from "formik";
import {
  addHabitationalPolicyChange,
  clearHabitationalPolicyChangeReqState,
  fetchHabitationalPolicyChangeReqAsync,
} from "../../../../../../redux/habitationalPolicy/habitationalPolicyChangeRequest/habitationalPolicyChangeReqActions";
import {
  ControlledCustomSelect,
  CustomFormLabel,
  CustomTextField,
} from "../../../../../../components/formsComponents";
import produce from "immer";
import {
  CustomDateAndTimePicker,
  CustomDatePicker,
} from "../../../../../../components/formsComponents/CustomDatePicker";
import { formatDate, toSentenceCase } from "../../../../../../helpers";
import { RoleBasedCustomButton } from "../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";

import { UsersAutoSearchByRole } from "../../../../../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import { useCurrentUserId } from "../../../../../../hooks/useAssignedTo";
import { PageContainer } from "../../../../../../components/container/PageContainer";
import { CreditCardInformation } from "./Steps/CreditCardInformation";
import { Underwriting } from "./Steps/Underwriting";
import { NameChangeInformation } from "./Steps/NameChange";
import { AddressChangeInformation } from "./Steps/AddressChange";
import { RiskLocationInformation } from "./Steps/RiskLocationInformation";
import {
  HabitationalPCRSubTypeOptions,
  otherCancellationReason,
  STANDARD_APP_DATE_FORMAT,
  TASK_STATUS,
} from "../../../../../../constants/constants";
import { InterestedPartyInformation } from "./Steps/InterestedPartyInformation";
import { CoverageInformation } from "./Steps/CoverageInformation";
import { ScheduleInformation } from "./Steps/ScheduleInformation";
import { PolicyChangeAttachmentUpload } from "./Steps/PolicyChangeAttachmentUpload";
import { IHabitationalPolicyChange } from "../../../../../../redux/habitationalPolicy/habitationalPolicyChangeRequest/habitationalPolicyChangeReq.types";
import { FieldSet } from "../../../../../../components/FieldSet/FieldSet";
import { useRoles } from "../../../../../../security/RolesProvider/RolesProvider";
import { PolicyChangeActivities } from "../../../../../../components/Activity/PolicyChangeActivities/PolicyChangeActivities";
import { showMessage } from "../../../../../../redux/messages/messagesActions";
import moment from "moment";
import { PublishedWithChanges } from "@mui/icons-material";
import { a11yProps } from "../../../../../../components/Tabs/utils";
import { TabPanel } from "../../../../../../components/Tabs/TabPanel";

export const HabitationalPolicyChangeReqForm: React.FC = () => {
  const { customerId, customerPolicyId, incrementalNumber } = useParams() as {
    customerId?: number;
    customerPolicyId?: number;
    incrementalNumber?: string;
  };

  const isUpdate = incrementalNumber ? true : false;
  const { policyChangeData, loading } = useSelector(
    (storeState: IStoreState) => storeState.habitational.policyChangeReq
  );

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const { fetchPoliciesByCustomerPolicyId } = usePoliciesByCustomer();

  const currentUserId = useCurrentUserId();
  const { firstName, lastName } = useRoles();
  const [buttonClicked, setButtonClicked] = React.useState<string | null>(null);
  const isCompletedRef = React.useRef(false);
  const [selectedTab, setSelectedTab] = React.useState(0);

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: policyChangeData,
    validate: (values) => {
      const errors: any = {};

      if (!values.type) {
        errors.type = "*Type is required.";
      }
      if (!values.sub_type) {
        errors.sub_type = "*Sub Type is required.";
      }
      if (!values.effective_date) {
        errors.effective_date = "*Effective Date is required.";
      }
      if (!values.due_date) {
        errors.due_date = "*Due Date is required.";
      }
      if (!values.date_created) {
        errors.date_created = "*Date Created is required.";
      }
      if (values.type === "CancellationReinstatement") {
        if (!values.cancellation_reason) {
          errors.cancellation_reason = "*Cancellation Reason is required.";
        }
      }

      if (
        isUpdate &&
        values.sub_type &&
        ["Address Change", "Multiple Change"].includes(values.sub_type)
      ) {
        if (!values.address_change.address_type) {
          errors["address_change.address_type"] = "*Address Type is required.";
          dispatch(
            showMessage({
              message: "Address Type is required",
              type: "error",
              displayAs: "dialog",
            })
          );
        }
      }

      // if (!values.effective_time) {
      //   errors.effective_time = "*Effective Time is required.";
      // }
      // if (
      //   values.status === "Completed" &&
      //   ["New", "Remarket(upload)"].includes(values.type as string)
      // ) {
      //   if (!values.assigned_policy_number) {
      //     errors.assigned_policy_number = "Policy number field is requried.";
      //   }
      // }

      return errors;
    },
    onSubmit: (values) => {
      if (customerId && customerPolicyId) {
        dispatch(
          addHabitationalPolicyChange(
            {
              ...values,
              customer_id: customerId,
              customer_policy_id: customerPolicyId,
            },
            policyChangeData,
            isUpdate,
            (isSuccess, data) => {
              if (isSuccess && data) {
                // removeAutoSavedDraft(draftUrl);
                if (buttonClicked && buttonClicked === "Continue") {
                  setValues(data);
                  navigate(
                    `/habitational-policy/${customerId}/policy-change-request/${customerPolicyId}/manage/${data.change_request_code}`,
                    { replace: true }
                  );
                } else {
                  navigate(
                    `/habitational-policy/${customerId}/policy-change-request-list/${customerPolicyId}`
                  );
                }
              }
            },
            {
              module_name: LOG_MODULES.HABITATIONAL_POLICY.moduleName,
              sub_module_name: LOG_MODULES.HABITATIONAL_POLICY.subModules.POLICY_CHANGE,
              action_name: "form_submit",
              executeOn: ["fulfilled"]
            }
          )
        );
      }
    },
  });

  const handleSelectTab = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleAutoSave = (finalValues: IHabitationalPolicyChange) => {
    if (incrementalNumber && customerId && customerPolicyId) {
      dispatch(
        addHabitationalPolicyChange(
          {
            ...finalValues,
            customer_id: customerId,
            customer_policy_id: customerPolicyId,
          },
          policyChangeData,
          isUpdate,
          (isSuccess, data) => {
            if (isSuccess && data) {
            }
          }
        )
      );
    }
  };

  const handleTypeChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;

    const defaultValues: Record<
      string,
      {
        sub_type: string;
      }
    > = {
      GeneralChange: {
        sub_type: "General Change",
      },
      PolicyChange: {
        sub_type: "Name Change",
      },
      New: {
        sub_type: "Home (Upload)",
      },
      PaymentMethod: {
        sub_type: "Payment Method Change",
      },
      // CancellationReinstatement: {
      //   sub_type: "FLAT",
      // },
      LOA: {
        sub_type: "LOA",
      },
      LOE: {
        sub_type: "LOE",
      },
    };

    const selectedDefaults = defaultValues[value] || {};

    const newValues = produce(values, (draftValues) => {
      draftValues.type = value;
      draftValues.sub_type = selectedDefaults?.sub_type;
      draftValues.assigned_policy_number = null;
      draftValues.change_mailing_address_on_customer = null;
      draftValues.address_change = {
        address: null,
        change_type: null,
        address_two: null,
        address_type: null,
        unit_number: null,
        city: null,
        country: null,
        postal_code: null,
        state: null,
        effective_date: null,
        phone: null,
        email: null,
        risk_location_updates: [],
      };
    });
    setValues(newValues);
  };

  const handleSubTypeChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    const newValues = produce(values, (draftValues) => {
      draftValues.sub_type = value;
      draftValues.name_change = {
        new_name: null,
        date_of_birth: null,
        relationship: null,
      };
      draftValues.change_mailing_address_on_customer = null;
      draftValues.address_change = {
        address: null,
        change_type: null,
        address_two: null,
        address_type: null,
        unit_number: null,
        city: null,
        country: null,
        postal_code: null,
        state: null,
        effective_date: null,
        phone: null,
        email: null,
        risk_location_updates: [],
      };
    });
    setValues(newValues);
  };

  const handleStatus = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;

    const newValues = produce(values, (draftValues) => {
      draftValues.status = value;
      draftValues.assigned_policy_number = null;
    });
    setValues(newValues);
  };



  React.useEffect(() => {
    (async () => {
      if (isUpdate) {
        if (policyChangeData.status === "Completed") {
          isCompletedRef.current = true;
        }
        setValues(policyChangeData);
      } else {
        if (customerId && customerPolicyId) {
          const policyOtherInfo = await fetchPoliciesByCustomerPolicyId(
            customerPolicyId
          );
          const policyInfo = Array.isArray(policyOtherInfo)
            ? policyOtherInfo[0]
            : null;
          if (policyInfo) {
            const newValues = produce(values, (draftValues) => {
              draftValues.customer_id = customerId;
              draftValues.customer_policy_id = customerPolicyId;
              draftValues.policy_number = policyInfo.policy_number;
              draftValues.customer_name = policyInfo.customer_name;
              draftValues.customer_code = policyInfo.customer_code;
              draftValues.policy_type = policyInfo.line_of_business;
              draftValues.branch_code = policyInfo.branch_code;
              draftValues.producer_one_code =
                policyInfo.producer_one_code || "--";
              draftValues.producer_one_id = policyInfo.producer_one || "--";
              draftValues.policy_status = policyInfo.policy_status;
              draftValues.insurer = policyInfo.insurer;
              draftValues.policy_effective_date = formatDate(
                policyInfo.effective_date
              );
              draftValues.effective_time = policyInfo.effective_time;
              draftValues.expiry_date = policyInfo.expiry_date;
              draftValues.current_policy_status =
                policyInfo.current_policy_status;
              draftValues.assigned_to_id = currentUserId as any;
              draftValues.assigned_to_name = firstName + " " + lastName;
            });
            setValues(newValues);
          }
        }
      }
    })();
  }, [policyChangeData]);

  React.useEffect(() => {
    if (customerPolicyId && incrementalNumber) {
      dispatch(
        fetchHabitationalPolicyChangeReqAsync(
          customerPolicyId,
          incrementalNumber
        )
      );
    }
  }, [incrementalNumber]);

  React.useEffect(() => {
    return () => {
      dispatch(clearHabitationalPolicyChangeReqState());
    };
  }, []);

  const originalDueDateRef = React.useRef<string | null>(null);

  const handleEffectiveDateChange = (newDate: string | null) => {
    setFieldValue("effective_date", newDate);

    if (values.date_created && newDate) {
      const created = moment(values.date_created, STANDARD_APP_DATE_FORMAT);
      const effective = moment(newDate);

      const diffDays = effective.diff(created, "days");

      // initialize original due date only once
      if (!originalDueDateRef.current) {
        originalDueDateRef.current = created
          .clone()
          .add(3, "days")
          .format("YYYY-MM-DD");
      }

      if (diffDays < 3) {
        // Case: effective date < 3 days → due_date = effective date
        setFieldValue("due_date", effective.format("YYYY-MM-DD"));
      } else {
        // Case: effective date ≥ 3 days → reset to original due date
        setFieldValue("due_date", originalDueDateRef.current);
      }
    }
  };

  React.useEffect(() => {
    if (values.effective_date) {
      handleEffectiveDateChange(values.effective_date);
    }
  }, [values.date_created, values.effective_date]);

  return (
    <PageLoader loading={loading === LoadState.InProgress}>
      <PageContainer
        title={isUpdate ? "Edit Policy Change Req" : "Create Policy Change Req"}
        description="Manage summary ."
      >

        {isUpdate && (
          <Tabs
            value={selectedTab}
            variant="fullWidth"
            scrollButtons="auto"
            onChange={handleSelectTab}
          >
            <Tab
              icon={<PublishedWithChanges />}
              iconPosition="start"
              label="Policy Change Request Habitational"
              {...a11yProps(0)}
            />
            <Tab
              sx={{ minHeight: "50px" }}
              icon={<PublishedWithChanges />}
              iconPosition="start"
              label="Activities"
              {...a11yProps(1)}
            />
          </Tabs>
        )}

        <TabPanel value={selectedTab} index={0}>
          <StandadCard
            sx={{ mt: 2 }}
            heading="Policy Change Request Habitational"
          >
            <form onSubmit={handleSubmit}>
              <FieldSet disabled={isCompletedRef.current}>
                <Grid container spacing={2} mb={5}>
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Customer Name
                      </Typography>
                      <Typography>{values.customer_name}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Customer Code
                      </Typography>
                      <Typography>{values.customer_code}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Policy
                      </Typography>
                      <Typography>{values.policy_type}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Branch Code
                      </Typography>
                      <Typography>{values.branch_code}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Producer 1
                      </Typography>
                      <Typography>{values.producer_one_code}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Policy Status
                      </Typography>
                      <Typography>{values.current_policy_status}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Policy Number
                      </Typography>
                      <Typography>{values.policy_number}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Insurer Code
                      </Typography>
                      <Typography>{values.insurer}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Effective Date
                      </Typography>
                      <Typography>
                        {formatDate(values.policy_effective_date)}
                      </Typography>
                    </Stack>
                  </Grid>
                  {/* <Grid item xs={12} md={2.4}>
                  <Stack display={"flex"} justifyContent={"space-between"}>
                    <Typography
                      variant="body1"
                      fontSize={"1rem"}
                      fontWeight={600}
                    >
                      Effective Time
                    </Typography>
                    <Typography>
                      {formatDateWithTime(values.effective_time)}
                    </Typography>
                  </Stack>
                </Grid> */}
                  <Grid item xs={12} md={2.4}>
                    <Stack display={"flex"} justifyContent={"space-between"}>
                      <Typography
                        variant="body1"
                        fontSize={"1rem"}
                        fontWeight={600}
                      >
                        Expiry Date
                      </Typography>
                      <Typography>{formatDate(values.expiry_date)}</Typography>
                    </Stack>
                  </Grid>
                </Grid>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={2.4}>
                    <CustomFormLabel>Type</CustomFormLabel>
                    <ControlledCustomSelect
                      variant="outlined"
                      size="small"
                      type="string"
                      fullWidth
                      displayEmpty
                      name="type"
                      value={values.type}
                      onChange={handleTypeChange}
                      placeholder="Select One"
                      options={[
                        { label: "General Change", value: "GeneralChange" },
                        { label: "New (upload)", value: "New" },
                        { label: "Policy Change", value: "PolicyChange" },
                        { label: "LOA", value: "LOA" },
                        { label: "LOE", value: "LOE" },
                        {
                          label: "Payment Method Change",
                          value: "PaymentMethod",
                        },
                        {
                          label: "Cancellation/Reinstatement",
                          value: "CancellationReinstatement",
                        },
                      ]}
                      error={errors.type ? true : false}
                      helperText={errors.type}
                    />
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <CustomFormLabel>Subtype</CustomFormLabel>
                    <ControlledCustomSelect
                      variant="outlined"
                      size="small"
                      type="string"
                      fullWidth
                      displayEmpty
                      name="sub_type"
                      value={values.sub_type}
                      onChange={handleSubTypeChange}
                      placeholder="Select One"
                      options={
                        HabitationalPCRSubTypeOptions[
                        values.type as keyof typeof HabitationalPCRSubTypeOptions
                        ] || []
                      }
                      error={errors.sub_type ? true : false}
                      helperText={errors.sub_type}
                    />
                  </Grid>
                  {values.type && values.type === "CancellationReinstatement" &&
                    <Grid item xs={12} lg={2.4}>
                      <CustomFormLabel>Cancel Reason</CustomFormLabel>
                      <ControlledCustomSelect
                        variant="outlined"
                        size="small"
                        type="string"
                        displayEmpty
                        name="cancellation_reason"
                        fullWidth
                        placeholder="Select One"
                        options={otherCancellationReason}
                        value={values.cancellation_reason}
                        onChange={handleChange}
                        error={errors.cancellation_reason ? true : false}
                        helperText={errors.cancellation_reason}
                      />
                    </Grid>
                  }
                  <Grid item xs={12} md={2.4}>
                    <CustomFormLabel>Date Created</CustomFormLabel>
                    <CustomDatePicker
                      fullWidth
                      disabled
                      value={values.date_created || ""}
                      onChange={(newValue, withTime) => {
                        setFieldValue("date_created", withTime);
                      }}
                      errorMessage={errors.date_created}
                    />
                  </Grid>
                  <Grid item xs={12} md={2.4}>
                    <CustomFormLabel>Effective Date & Time</CustomFormLabel>
                    <CustomDateAndTimePicker
                      fullWidth
                      value={values.effective_date || null}
                      onChange={(newDate) => {
                        setFieldValue("effective_date", newDate);
                      }}
                      errorMessage={errors.effective_date}
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <CustomFormLabel>Days Left</CustomFormLabel>
                    <CustomTextField
                      fullWidth
                      id="days_left"
                      name="days_left"
                      value={values.days_left || ""}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={1.9}>
                    <CustomFormLabel>Due Date</CustomFormLabel>
                    <CustomDatePicker
                      fullWidth
                      value={values.due_date || ""}
                      onChange={(newValue) => {
                        setFieldValue("due_date", newValue);
                      }}
                      errorMessage={errors.due_date}
                    />
                  </Grid>
                  <Grid item xs={12} md={1.9}>
                    <CustomFormLabel>Date Completed</CustomFormLabel>
                    <CustomDatePicker
                      fullWidth
                      disabled
                      value={values.date_completed || ""}
                      onChange={(newValue) => {
                        setFieldValue("date_completed", newValue);
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={2.4}>
                    <UsersAutoSearchByRole
                      label="Assigned To"
                      value={values.assigned_to_id}
                      errorMessage={errors.assigned_to_id}
                      onSelect={(data) => {
                        setFieldValue("assigned_to_id", data.user_fact_id);
                        setFieldValue(
                          "assigned_to_name",
                          data.first_name + " " + data.last_name
                        );
                      }}
                    />
                  </Grid>
                  {values.type === "PolicyChange" && values.sub_type === "Address Change" && (
                    <Grid item xs={12} md={6}>
                      <CustomFormLabel>Change Mailing address on customer screen as well?</CustomFormLabel>
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          name="change_mailing_address_on_customer"
                          value={values.change_mailing_address_on_customer}
                          onChange={handleChange}
                        >
                          <FormControlLabel value="Y" control={<Radio />} label="Yes" />
                          <FormControlLabel value="N" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ mt: 3, mb: 1 }} />
              </FieldSet>

              {!isUpdate && (
                <Box>
                  <Button
                    variant="contained"
                    type="submit"
                    onClick={() => setButtonClicked("Continue")}
                  >
                    Continue
                  </Button>
                </Box>
              )}
              {isUpdate && (
                <>
                  <FieldSet disabled={isCompletedRef.current}>
                    {values.sub_type &&
                      [
                        "Payment Method Change",
                        "General Change",
                        "Home (Upload)",
                        "Home (Remarket)",
                        "Auto + Home (Upload)",
                        "Auto + Home (Remarket)",
                        "Cancellation/Reinstatement",
                        "LOA",
                        "LOE",
                      ].includes(values.sub_type) && (
                        <>
                          <CreditCardInformation
                            values={values}
                            errors={errors}
                            handleChange={handleChange}
                            setValues={setValues}
                            setFieldValue={setFieldValue}
                            isStatusCompleted={isCompletedRef.current}
                          />
                        </>
                      )}

                    {values.sub_type && values.sub_type === "Name Change" && (
                      <NameChangeInformation
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                        setValues={setValues}
                        handleChange={handleChange}
                        isStatusCompleted={isCompletedRef.current}
                      />
                    )}

                    {values.sub_type && values.sub_type === "Address Change" && (
                      <AddressChangeInformation
                        values={values}
                        errors={errors}
                        setFieldValue={setFieldValue}
                        setValues={setValues}
                        handleChange={handleChange}
                        isStatusCompleted={isCompletedRef.current}
                      />
                    )}
                  </FieldSet>

                  {values.sub_type &&
                    values.sub_type === "Risk Location Change" && (
                      <>
                        <RiskLocationInformation
                          values={values}
                          errors={errors}
                          customerPolicyId={customerPolicyId}
                          customerId={customerId}
                          setFieldValue={setFieldValue}
                          setValues={setValues}
                          handleChange={handleChange}
                          autoSaveData={handleAutoSave}
                          isStatusCompleted={isCompletedRef.current}
                        />
                        <InterestedPartyInformation
                          values={values}
                          errors={errors}
                          customerPolicyId={customerPolicyId}
                          customerId={customerId}
                          setFieldValue={setFieldValue}
                          setValues={setValues}
                          handleChange={handleChange}
                          autoSaveData={handleAutoSave}
                          isStatusCompleted={isCompletedRef.current}
                        />
                        <CoverageInformation
                          values={values}
                          errors={errors}
                          customerPolicyId={customerPolicyId}
                          customerId={customerId}
                          setFieldValue={setFieldValue}
                          setValues={setValues}
                          handleChange={handleChange}
                          autoSaveData={handleAutoSave}
                          isStatusCompleted={isCompletedRef.current}
                        />
                        <ScheduleInformation
                          values={values}
                          errors={errors}
                          customerPolicyId={customerPolicyId}
                          customerId={customerId}
                          setFieldValue={setFieldValue}
                          setValues={setValues}
                          handleChange={handleChange}
                          autoSaveData={handleAutoSave}
                          isStatusCompleted={isCompletedRef.current}
                        />
                      </>
                    )}

                  {values.sub_type &&
                    values.sub_type === "Interested Party Change" && (
                      <>
                        <InterestedPartyInformation
                          values={values}
                          errors={errors}
                          customerPolicyId={customerPolicyId}
                          customerId={customerId}
                          setFieldValue={setFieldValue}
                          setValues={setValues}
                          handleChange={handleChange}
                          autoSaveData={handleAutoSave}
                          isStatusCompleted={isCompletedRef.current}
                        />
                        <ScheduleInformation
                          values={values}
                          errors={errors}
                          customerPolicyId={customerPolicyId}
                          customerId={customerId}
                          setFieldValue={setFieldValue}
                          setValues={setValues}
                          handleChange={handleChange}
                          autoSaveData={handleAutoSave}
                          isStatusCompleted={isCompletedRef.current}
                        />
                      </>
                    )}

                  {values.sub_type && values.sub_type === "Coverage Change" && (
                    <>
                      <CoverageInformation
                        values={values}
                        errors={errors}
                        customerPolicyId={customerPolicyId}
                        customerId={customerId}
                        setFieldValue={setFieldValue}
                        setValues={setValues}
                        handleChange={handleChange}
                        autoSaveData={handleAutoSave}
                        isStatusCompleted={isCompletedRef.current}
                      />
                    </>
                  )}

                  {values.sub_type &&
                    values.sub_type === "Schedule Item Change" && (
                      <>
                        <ScheduleInformation
                          values={values}
                          errors={errors}
                          customerPolicyId={customerPolicyId}
                          customerId={customerId}
                          setFieldValue={setFieldValue}
                          setValues={setValues}
                          handleChange={handleChange}
                          autoSaveData={handleAutoSave}
                          isStatusCompleted={isCompletedRef.current}
                        />
                      </>
                    )}

                  {/* ---------Interested Party Change ----------*/}

                  {/* -----------Schedule Item Change------------- */}
                  <Underwriting
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    setValues={setValues}
                    setFieldValue={setFieldValue}
                    autoSaveData={handleAutoSave}
                    isStatusCompleted={isCompletedRef.current}
                  />
                  <>
                    <PolicyChangeAttachmentUpload
                      values={values}
                      errors={errors}
                      customerPolicyId={customerPolicyId}
                      customerId={customerId}
                      setFieldValue={setFieldValue}
                      setValues={setValues}
                      handleChange={handleChange}
                      isStatusCompleted={isCompletedRef.current}
                    />
                  </>
                  <FieldSet disabled={isCompletedRef.current}>
                    <Grid item xs={12}>
                      <CustomFormLabel>Remarks</CustomFormLabel>
                      <CustomTextField
                        name="remarks"
                        variant="outlined"
                        size="small"
                        multiline
                        rows={3}
                        type="text"
                        fullWidth
                        value={values.remarks}
                        onChange={handleChange}
                      />
                    </Grid>
                  </FieldSet>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <CustomFormLabel>Status</CustomFormLabel>

                      <ControlledCustomSelect
                        fullWidth
                        value={toSentenceCase(values.status)}
                        name="status"
                        onChange={handleChange}
                        placeholder="Select one"
                        disabled={isCompletedRef.current}
                        displayEmpty
                        options={TASK_STATUS}
                      />
                    </Grid>
                    {/* {values.status === "Completed" &&
                    ["New"].includes(values.type as string) && (
                      <Grid item xs={12} md={6}>
                        <CustomFormLabel>
                          Assigned Policy Number
                        </CustomFormLabel>

                        <CustomTextField
                          name="assigned_policy_number"
                          id="assigned_policy_number"
                          value={values.assigned_policy_number}
                          onChange={handleChange}
                          fullWidth
                          error={errors.assigned_policy_number ? true : false}
                          helperText={errors.assigned_policy_number}
                        />
                      </Grid>
                    )} */}
                  </Grid>

                  <Stack direction={"row"} spacing={2} marginTop={3}>
                    <RoleBasedCustomButton
                      moduleKey={MODULE_IDS.HABITATIONAL}
                      type="submit"
                      variant="contained"
                      disabled={isCompletedRef.current}
                      onClick={() => setButtonClicked("save")}
                    >
                      Save
                    </RoleBasedCustomButton>
                  </Stack>
                </>
              )}
            </form>
          </StandadCard>
        </TabPanel>

        <TabPanel value={selectedTab} index={1}>
          {incrementalNumber && values.change_request_code && (
            <Box mt={4}>
              <PolicyChangeActivities
                moduleInformation={{
                  moduleId: values.change_request_code,
                  moduleName: MODULE_NAMES.POLICY_CHANGE,
                  subModuleName: MODULE_NAMES.POLICY_CHANGE,
                  refColumnName: "change_request_code",
                }}
                customerId={customerId}
                customerPolicyId={customerPolicyId}
              />
            </Box>
          )}
        </TabPanel>



      </PageContainer>
    </PageLoader>
  );
};
